/**
 * Frontend mirror of pms-api-v2 `pms-content-studio/recipe-pricing.ts`.
 * Resolves the live credit cost of a generation from a recipe + the
 * vendor's current input selections + the linked product's
 * visualizationType. Used by the Recipe node's live cost preview so the
 * number the vendor sees as they toggle resolution / autoPick / mode /
 * duration matches what the backend will reserve at submit time.
 *
 * KEEP IN SYNC with the backend file. Any change to the formula must
 * land on both sides — they're the same algorithm against the same
 * recipe `creditCostMultipliers` shape served by the API.
 */

import { extensionCostFactor } from './recipe-schema/recipe-extensions';

export interface RecipePricingShape {
    /** Lets controls added in code (recipe-extensions.ts) price themselves. */
    slug?: string;
    creditCost: number;
    creditCostMultipliers?: {
        resolution?: Record<string, number>;
        autoPick?: number;
        mode?: Record<string, number>;
        visualizationType?: Record<string, number>;
        durationLinear?: { input: string; baseValue: number };
        phase?: Record<string, number>;
        /** Multipliers keyed by any input's value; `_default` when the input is missing. */
        inputs?: Record<string, Record<string, number>>;
    };
}

export interface PricingContext {
    inputs?: Record<string, any>;
    visualizationType?: string;
}

export function resolveCreditCost(
    recipe: RecipePricingShape,
    ctx: PricingContext = {},
): number {
    const base = Number(recipe?.creditCost) || 0;
    // Explicitly-free recipes (creditCost 0, e.g. design-render) stay free.
    if (base <= 0) return 0;
    const m = recipe?.creditCostMultipliers || {};
    const inputs = ctx.inputs || {};
    let factor = 1;

    if (m.resolution && typeof inputs.resolution === 'string') {
        const r = m.resolution[inputs.resolution];
        if (typeof r === 'number' && r > 0) factor *= r;
    }
    if (typeof m.autoPick === 'number' && m.autoPick > 0) {
        if (inputs.autoPick === true || inputs.autoPick === 'true') {
            factor *= m.autoPick;
        }
    }
    if (m.mode && typeof inputs.mode === 'string') {
        const r = m.mode[inputs.mode];
        if (typeof r === 'number' && r > 0) factor *= r;
    }
    if (m.visualizationType && typeof ctx.visualizationType === 'string') {
        const r = m.visualizationType[ctx.visualizationType];
        if (typeof r === 'number' && r > 0) factor *= r;
    }
    if (m.durationLinear) {
        const v = Number(inputs[m.durationLinear.input]);
        const baseV = Number(m.durationLinear.baseValue);
        if (Number.isFinite(v) && v > 0 && Number.isFinite(baseV) && baseV > 0) {
            factor *= v / baseV;
        }
    }
    if (m.phase && typeof inputs.phase === 'string') {
        const r = m.phase[inputs.phase];
        if (typeof r === 'number' && r > 0) factor *= r;
    }
    if (m.inputs) {
        for (const [key, table] of Object.entries(m.inputs)) {
            const v = inputs[key];
            const r = v === undefined || v === null || v === '' ? table._default : table[String(v)];
            if (typeof r === 'number' && r > 0) factor *= r;
        }
    }

    factor *= extensionCostFactor(recipe?.slug, inputs, ctx.visualizationType);
    const finalCredits = Math.ceil(base * factor);
    return Math.max(1, finalCredits);
}
